import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig, isMobileBreakpoint } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import '@theme/scrolling';

const CART_DRAWER_UPSELLS_STORAGE_KEY = 'hoover:cart-drawer-upsells';
const BOGOS_CART_UPSELL_ROOT_SELECTOR = '.bogos-cart-upsell-root[data-surface="cart_drawer"]';
const BOGOS_CART_UPSELL_WIDGET_SELECTOR = '.bogos-cart-upsell-widget';
const BOGOS_CART_UPSELL_CARD_SELECTOR = '.bogos-cart-upsell-card';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | undefined} */
  #recentlyAddedTimeout;

  /** @type {AbortController | null} */
  #upsellsAbortController = null;

  /** @type {Array<any>} */
  #currentUpsellProducts = [];

  /** @type {string | null} */
  #currentUpsellProductId = null;

  /** @type {MutationObserver} */
  #klaviyoBisObserver = new MutationObserver(() => this.#removeKlaviyoBisButtons());

  /** @type {MutationObserver} */
  #bogosUpsellObserver = new MutationObserver(() => this.#scheduleBogosUpsellSync());

  /** @type {boolean} */
  #animateNextBogosUpsell = false;

  /** @type {number | undefined} */
  #bogosUpsellSyncFrame;

  /** @type {string} */
  #bogosUpsellMarkupSignature = '';

  /** @type {number | undefined} */
  #upsellsHideTimeout;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleUpsellDotClick);
    this.addEventListener('click', this.#handleDrawerCloseClick);
    this.addEventListener('scroll', this.#handleUpsellScroll, true);
    this.addEventListener('submit', this.#handleUpsellSubmit);
    this.#klaviyoBisObserver.observe(this, { childList: true, subtree: true });
    this.#removeKlaviyoBisButtons();
    this.#clearStoredUpsells();
    this.#syncBogosUpsells();
    this.#bogosUpsellObserver.observe(document.body, { childList: true, subtree: true });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleUpsellDotClick);
    this.removeEventListener('click', this.#handleDrawerCloseClick);
    this.removeEventListener('scroll', this.#handleUpsellScroll, true);
    this.removeEventListener('submit', this.#handleUpsellSubmit);
    this.#historyAbortController?.abort();
    this.#upsellsAbortController?.abort();
    this.#klaviyoBisObserver.disconnect();
    this.#bogosUpsellObserver.disconnect();

    if (this.#recentlyAddedTimeout) window.clearTimeout(this.#recentlyAddedTimeout);
    if (this.#bogosUpsellSyncFrame) window.cancelAnimationFrame(this.#bogosUpsellSyncFrame);
    if (this.#upsellsHideTimeout) window.clearTimeout(this.#upsellsHideTimeout);
  }

  #removeKlaviyoBisButtons() {
    this.querySelectorAll('.cart-drawer__dialog .hoover-klaviyo-bis-button').forEach((button) => button.remove());
  }

  #handleBogosResize = () => {
    this.#syncBogosUpsells({ animate: false });
  };

  #scheduleBogosUpsellSync() {
    if (this.#bogosUpsellSyncFrame) return;

    this.#bogosUpsellSyncFrame = window.requestAnimationFrame(() => {
      this.#bogosUpsellSyncFrame = undefined;
      this.#syncBogosUpsells({ animate: false });
    });
  }

  /** @param {{ animate?: boolean }} [options] */
  #syncBogosUpsells(options = {}) {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const root = document.querySelector(BOGOS_CART_UPSELL_ROOT_SELECTOR);
    const shouldAnimate = options.animate === true || this.#animateNextBogosUpsell;
    const hadUpsells = this.#hasVisibleUpsells();
    const cards = root instanceof HTMLElement ? this.#getOrderedBogosCards(root) : [];
    const renderedCards = cards.map((card) => this.#renderBogosSourceCard(card)).filter((card) => card !== null);
    const desktopCards = renderedCards.slice(0, 6);
    const desktopMarkup = desktopCards.map((card) => card.markup).join('');
    const mobileCards = renderedCards.slice(0, 5);
    const mobileMarkup = mobileCards.map((card) => card.markup).join('');
    const signature = `${renderedCards.map((card) => card.variantId).join(',')}:${desktopMarkup}:${mobileMarkup}`;
    const hasUpsells = renderedCards.length > 0;

    if (signature !== this.#bogosUpsellMarkupSignature) {
      this.#bogosUpsellMarkupSignature = signature;
      dialog.querySelectorAll('.cart-drawer__upsells-desktop .cart-drawer__upsells-list').forEach((list) => {
        list.innerHTML = desktopMarkup;
      });
      dialog.querySelectorAll('.cart-drawer__upsells-mobile .cart-drawer__upsells-list').forEach((list) => {
        list.innerHTML = mobileMarkup;
      });
      this.#renderUpsellDots(mobileCards.length);
    }

    this.#setUpsellsVisible(hasUpsells, { animate: shouldAnimate && !hadUpsells });

    if (hasUpsells) this.#animateNextBogosUpsell = false;
  }

  /** @param {Element} card */
  #renderBogosSourceCard(card) {
    const image = card.querySelector('img');
    const form = card.querySelector('form');
    const variantInput = form?.querySelector('input[name="id"]') || card.querySelector('input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement
      ? variantInput.value
      : card.querySelector('[data-variant-id], [data-variant]')?.getAttribute('data-variant-id') || card.querySelector('[data-variant-id], [data-variant]')?.getAttribute('data-variant') || '';
    if (!variantId) return '';

    const link = card.querySelector('a[href]');
    const url = link instanceof HTMLAnchorElement ? link.href : '#';
    const title = this.#getBogosCardTitle(card, link);
    const price = this.#getBogosCardPrice(card);
    const imageSrc = image instanceof HTMLImageElement ? this.#normalizeImageUrl(image.getAttribute('src') || image.src, 160) : '';
    const imageAlt = image instanceof HTMLImageElement ? image.alt || title : title;

    return {
      variantId,
      markup: `
      <article class="cart-drawer__upsell-card">
        <div class="cart-drawer__upsell-main">
          <a class="cart-drawer__upsell-media" href="${this.#escapeAttribute(url)}" tabindex="-1">
            ${imageSrc ? `<img loading="lazy" src="${this.#escapeAttribute(imageSrc)}" alt="${this.#escapeAttribute(imageAlt)}">` : ''}
          </a>
          <div class="cart-drawer__upsell-copy">
            <a class="cart-drawer__upsell-title" href="${this.#escapeAttribute(url)}">${this.#escapeHtml(title)}</a>
          </div>
        </div>
        <div class="cart-drawer__upsell-bottom">
          <span class="cart-drawer__upsell-prices">
            ${price ? `<span class="cart-drawer__upsell-price">${this.#escapeHtml(price)}</span>` : ''}
          </span>
          <form method="post" action="${Theme.routes.cart_add_url}" class="cart-drawer__upsell-form">
            <input type="hidden" name="id" value="${this.#escapeAttribute(variantId)}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="cart-drawer__upsell-add">
              <span aria-hidden="true">+</span>
              <span class="cart-drawer__upsell-add-label">Add</span>
              <span class="cart-drawer__upsell-spinner" aria-hidden="true"></span>
            </button>
          </form>
        </div>
      </article>
    `,
    };
  }

  /** @param {HTMLElement} root */
  #getOrderedBogosCards(root) {
    const widgets = Array.from(root.querySelectorAll(BOGOS_CART_UPSELL_WIDGET_SELECTOR));
    if (!widgets.length) return Array.from(root.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR));

    return widgets.reverse().flatMap((widget) => Array.from(widget.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR)));
  }

  /**
   * @param {Element} card
   * @param {Element | null} link
   */
  #getBogosCardTitle(card, link) {
    const titleElement = card.querySelector('.bogos-cart-upsell-product-title, .bogos-cart-upsell-card-title, .bogos-cart-upsell-name, [class*="title"], [class*="name"]');
    const title = titleElement?.textContent?.trim() || link?.textContent?.trim() || card.querySelector('img')?.getAttribute('alt')?.trim();
    return title
      ?.replace(/^\s*\d+\s*(?:x|×)\s*/i, '')
      .replace(/\s*(?:x|×)\s*\d+\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim() || 'Recommended product';
  }

  /** @param {Element} card */
  #getBogosCardPrice(card) {
    const priceElement = card.querySelector('.bogos-cart-upsell-price, .bogos-cart-upsell-product-price, .bogos-cart-upsell-card-price, [class*="price"]');
    const priceText = priceElement?.textContent?.trim() || card.textContent?.match(/\$\s?\d+(?:\.\d{2})?/)?.[0];
    return priceText || '';
  }

  /**
   * @param {string} url
   * @param {number} width
   */
  #normalizeImageUrl(url, width) {
    if (!url) return '';

    const normalizedUrl = new URL(url, window.location.origin);
    normalizedUrl.searchParams.set('width', width.toString());
    return normalizedUrl.toString();
  }

  /** @param {HTMLElement} root */
  #prepareBogosUpsells(root) {
    const header = root.querySelector('.bogos-cart-upsell-header');
    const title = root.querySelector('.bogos-cart-upsell-title');
    const list = root.querySelector('.bogos-cart-upsell-list');

    if (header instanceof HTMLElement) {
      const expanded = root.dataset.bogosMobileCollapsed === 'true' ? 'false' : 'true';

      if (header.getAttribute('role') !== 'button') header.setAttribute('role', 'button');
      if (header.getAttribute('tabindex') !== '0') header.setAttribute('tabindex', '0');
      if (header.getAttribute('aria-expanded') !== expanded) header.setAttribute('aria-expanded', expanded);
    }

    if (title instanceof HTMLElement && title.textContent !== 'Top Picks To Add On') {
      title.textContent = 'Top Picks To Add On';
    }

    if (list instanceof HTMLElement) {
      list.addEventListener('scroll', this.#handleBogosCarouselScroll, { passive: true });
    }

    this.#renderBogosDots(root);
  }

  #handleBogosUpsellClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const dot = target?.closest('[data-bogos-upsell-dot]');

    if (dot instanceof HTMLElement) {
      const root = dot.closest(BOGOS_CART_UPSELL_ROOT_SELECTOR);
      const list = root?.querySelector('.bogos-cart-upsell-list');
      const cards = root?.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR);
      const card = cards?.[Number(dot.dataset.bogosUpsellDot || 0)];

      if (list instanceof HTMLElement && card instanceof HTMLElement) {
        list.scrollTo({ left: card.offsetLeft - list.offsetLeft, behavior: 'smooth' });
      }

      return;
    }

    const header = target?.closest('.bogos-cart-upsell-header');
    if (!(header instanceof HTMLElement) || !isMobileBreakpoint()) return;

    const root = header.closest(BOGOS_CART_UPSELL_ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) return;

    const isCollapsed = root.dataset.bogosMobileCollapsed === 'true';
    root.dataset.bogosMobileCollapsed = isCollapsed ? 'false' : 'true';
    header.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
  };

  #handleBogosUpsellKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target instanceof Element ? event.target : null;
    const header = target?.closest('.bogos-cart-upsell-header');
    if (!(header instanceof HTMLElement) || !isMobileBreakpoint()) return;

    event.preventDefault();
    header.click();
  };

  #handleBogosCarouselScroll = (event) => {
    const list = event.target instanceof HTMLElement ? event.target : null;
    const root = list?.closest(BOGOS_CART_UPSELL_ROOT_SELECTOR);
    if (!(root instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    this.#setActiveBogosDot(root, this.#getClosestBogosCardIndex(root, list));
  };

  /** @param {HTMLElement} root */
  #renderBogosDots(root) {
    const listWrap = root.querySelector('.bogos-cart-upsell-list-wrap');
    const cards = root.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR);
    if (!(listWrap instanceof HTMLElement) || cards.length < 2) return;

    let dots = root.querySelector('.cart-drawer__bogos-dots');
    if (!(dots instanceof HTMLElement)) {
      dots = document.createElement('div');
      dots.className = 'cart-drawer__bogos-dots';
      listWrap.after(dots);
    }

    if (dots.childElementCount === cards.length) return;

    const dotsMarkup = Array.from({ length: cards.length }, (_, index) => (
      `<button type="button" class="cart-drawer__bogos-dot${index === 0 ? ' cart-drawer__bogos-dot--active' : ''}" data-bogos-upsell-dot="${index}" aria-label="Show upsell ${index + 1}"></button>`
    )).join('');

    if (dots.innerHTML !== dotsMarkup) dots.innerHTML = dotsMarkup;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} list
   */
  #getClosestBogosCardIndex(root, list) {
    const cards = Array.from(root.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR));
    if (!cards.length) return 0;

    return cards.reduce((closestIndex, card, cardIndex) => {
      const closestDistance = Math.abs(cards[closestIndex].getBoundingClientRect().left - list.getBoundingClientRect().left);
      const cardDistance = Math.abs(card.getBoundingClientRect().left - list.getBoundingClientRect().left);
      return cardDistance < closestDistance ? cardIndex : closestIndex;
    }, 0);
  }

  /**
   * @param {HTMLElement} root
   * @param {number} index
   */
  #setActiveBogosDot(root, index) {
    root.querySelectorAll('.cart-drawer__bogos-dot').forEach((dot, dotIndex) => {
      dot.classList.toggle('cart-drawer__bogos-dot--active', dotIndex === index);
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const isCartItemsUpdate = event.detail.data?.source === 'cart-items-component';
    const isAddToCart = event.detail.data?.itemCount !== undefined && !isCartItemsUpdate;
    const upsellRatings = this.#snapshotUpsellRatings();

    const dialog = document.querySelector('.cart-drawer__dialog');
    if (isCartItemsUpdate && event.detail.data?.itemCount === 0) {
      dialog?.removeAttribute('data-added-state');
      dialog?.classList.add('cart-drawer--empty');
    } else if (isAddToCart || (isCartItemsUpdate && event.detail.data?.itemCount > 0)) {
      dialog?.classList.remove('cart-drawer--empty');
    }

    if (isAddToCart && !event.detail.data?.didError) {
      const isCartDrawerUpsell = event.detail.data?.source === 'cart-drawer-upsell';

      this.#showAddedState();
      this.showDialog();
      this.#markRecentlyAddedItem(event.detail.data?.variantId || event.detail.sourceId);
      if (!isCartDrawerUpsell) {
        this.#animateNextBogosUpsell = true;
        this.#scheduleBogosUpsellSync();
        window.setTimeout(() => this.#syncBogosUpsells({ animate: true }), 250);
        window.setTimeout(() => this.#syncBogosUpsells({ animate: true }), 750);
      }
    }

    this.#announceCartCount(event.detail.resource?.item_count);
    this.#scheduleUpsellRatingsRestore(upsellRatings);

    if (isCartItemsUpdate) {
      window.requestAnimationFrame(() => this.#syncBogosUpsells({ animate: false }));
    }
  };

  /**
   * Highlights the item that was just added. Falls back to the first row because Shopify returns newest items first.
   * @param {string | undefined} variantId
   */
  #markRecentlyAddedItem(variantId) {
    window.requestAnimationFrame(() => {
      this.#showAddedState();

      const rows = this.refs.dialog?.querySelectorAll('.cart-items__table-row');
      if (!rows?.length) return;

      for (const row of rows) row.removeAttribute('data-recently-added');

      const numericVariantId = variantId && /^\d+$/.test(variantId) ? variantId : undefined;
      const row = numericVariantId
        ? this.refs.dialog?.querySelector(`.cart-items__table-row[data-variant-id="${numericVariantId}"]`)
        : rows[0];

      row?.setAttribute('data-recently-added', 'true');

      if (isMobileBreakpoint()) {
        row?.parentElement?.prepend(row);
        this.refs.dialog?.querySelector('.cart-drawer__items')?.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (this.#recentlyAddedTimeout) window.clearTimeout(this.#recentlyAddedTimeout);
      this.#recentlyAddedTimeout = window.setTimeout(() => {
        row?.removeAttribute('data-recently-added');
        this.refs.dialog?.removeAttribute('data-added-state');
      }, 5000);
    });
  }

  #showAddedState() {
    const apply = () => document.querySelector('.cart-drawer__dialog')?.setAttribute('data-added-state', 'true');
    apply();
    window.requestAnimationFrame(apply);
    window.requestAnimationFrame(() => window.requestAnimationFrame(apply));
    window.setTimeout(apply, 100);
  }

  /**
   * @param {Event} event
   */
  #handleUpsellDotClick = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-upsell-dot]') : null;
    if (!(dot instanceof HTMLElement)) return;

    const disclosure = dot.closest('.cart-drawer__upsells-disclosure');
    const list = disclosure?.querySelector('.cart-drawer__upsells-list');
    const cards = disclosure?.querySelectorAll('.cart-drawer__upsell-card');
    const index = Number(dot.dataset.upsellDot || 0);
    const card = cards?.[index];

    if (!(list instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

    list.scrollTo({ left: card.offsetLeft - list.offsetLeft, behavior: 'smooth' });
    this.#setActiveUpsellDot(disclosure, index);
  };

  /** @param {Event} event */
  #handleDrawerCloseClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-drawer-close]') : null;
    if (!button) return;

    event.preventDefault();
    this.close();
  };

  /**
   * @param {Event} event
   */
  #handleUpsellScroll = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.classList.contains('cart-drawer__upsells-list')) return;

    const list = event.target;
    const disclosure = list.closest('.cart-drawer__upsells-disclosure');
    const cards = Array.from(list.querySelectorAll('.cart-drawer__upsell-card'));
    if (!disclosure || !cards.length) return;

    const index = cards.reduce((closestIndex, card, cardIndex) => {
      const closestDistance = Math.abs(cards[closestIndex].getBoundingClientRect().left - list.getBoundingClientRect().left);
      const cardDistance = Math.abs(card.getBoundingClientRect().left - list.getBoundingClientRect().left);
      return cardDistance < closestDistance ? cardIndex : closestIndex;
    }, 0);

    this.#setActiveUpsellDot(disclosure, index);
  };

  /**
   * Adds upsell products without leaving the drawer.
   * @param {SubmitEvent} event
   */
  #handleUpsellSubmit = async (event) => {
    const form = event.target instanceof Element ? event.target.closest('.cart-drawer__upsell-form') : null;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    const button = form.querySelector('.cart-drawer__upsell-add');
    const formData = new FormData(form);
    const sectionIds = this.#getCartSectionIds();
    const quantity = Number(formData.get('quantity')) || 1;

    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    this.#setUpsellButtonState(button, 'loading');

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add item to cart');
      }

      const itemCount = this.#getCartItemCountFromSections(data.sections, sectionIds) ?? quantity;
      const upsellRatings = this.#snapshotUpsellRatings();
      this.#setUpsellButtonState(button, 'added');
      await new Promise((resolve) => window.setTimeout(resolve, 900));

      this.dispatchEvent(
        new CartAddEvent({ item_count: itemCount }, data.variant_id?.toString() || formData.get('id')?.toString(), {
          source: 'cart-drawer-upsell',
          itemCount: quantity,
          productId: data.product_id?.toString(),
          variantId: data.variant_id?.toString(),
          sections: data.sections,
        })
      );

      this.#restoreUpsellRatings(upsellRatings);
      this.#scheduleUpsellRatingsRestore(upsellRatings);
    } catch (error) {
      console.error(error);
      this.#setUpsellButtonState(button, 'error');
    }
  };

  /**
   * @param {Element} disclosure
   * @param {number} index
   */
  #setActiveUpsellDot(disclosure, index) {
    disclosure.querySelectorAll('.cart-drawer__upsells-dot').forEach((dot, dotIndex) => {
      dot.classList.toggle('cart-drawer__upsells-dot--active', dotIndex === index);
    });
  }

  /**
   * Updates cart drawer upsells from Search & Discovery complementary recommendations.
   * @param {string | number | undefined} productId
   * @param {{ cartItems?: Array<any>, animate?: boolean }} [options]
   */
  async #updateRecommendedUpsells(productId, options = {}) {
    const productIds = this.#getCartProductIds(options.cartItems, productId);

    if (!productIds.length) {
      this.#clearStoredUpsells();
      this.#setUpsellProducts([], { animate: options.animate !== false });
      return;
    }

    this.#upsellsAbortController?.abort();
    this.#upsellsAbortController = new AbortController();
    this.#setUpsellProducts([], { animate: options.animate !== false });

    try {
      for (const sourceProductId of productIds) {
        const products = await this.#fetchRecommendedProducts(sourceProductId, this.#upsellsAbortController.signal);

        if (products.length) {
          this.#storeUpsells(sourceProductId, products);
          this.#setUpsellProducts(products, {
            animate: options.animate !== false,
            sourceProductId,
          });
          return;
        }
      }

      this.#clearStoredUpsells();
      this.#setUpsellProducts([], { animate: options.animate !== false });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('Failed to update cart drawer recommendations:', error);
    } finally {
      this.#upsellsAbortController = null;
    }
  }

  /** @param {{ animate?: boolean, cartItems?: Array<any> }} [options] */
  #hydrateStoredUpsells(options = {}) {
    const storedUpsells = this.#getStoredUpsells();
    const productIds = this.#getCartProductIds(options.cartItems, storedUpsells?.productId);

    if (
      this.#currentUpsellProducts.length &&
      this.#currentUpsellProductId &&
      productIds.includes(this.#currentUpsellProductId)
    ) {
      this.#setUpsellProducts(this.#currentUpsellProducts, { animate: options.animate !== false });
      return;
    }

    this.#setUpsellProducts([], { animate: options.animate !== false });

    if (!productIds.length) {
      this.#clearStoredUpsells();
      return;
    }

    this.#updateRecommendedUpsells(productIds[0], { cartItems: options.cartItems, animate: options.animate !== false });
  }

  /**
   * @param {Array<any> | undefined} cartItems
   * @param {string | number | undefined} preferredProductId
   * @returns {string[]}
   */
  #getCartProductIds(cartItems, preferredProductId) {
    const preferredId = preferredProductId?.toString();

    if (!Array.isArray(cartItems)) {
      return preferredId ? [preferredId] : [];
    }

    const ids = [];

    cartItems.forEach((item) => {
      const id = item?.product_id?.toString();
      if (id && !ids.includes(id)) ids.push(id);
    });

    if (preferredId && ids.includes(preferredId)) {
      return [preferredId, ...ids.filter((id) => id !== preferredId)];
    }

    return ids;
  }

  /** @returns {{ productId: string } | null} */
  #getStoredUpsells() {
    try {
      const value = window.localStorage.getItem(CART_DRAWER_UPSELLS_STORAGE_KEY);
      if (!value) return null;

      const parsed = JSON.parse(value);
      if (!parsed || !parsed.productId) return null;

      return parsed;
    } catch (_error) {
      return null;
    }
  }

  /**
   * @param {string | number} productId
   * @param {Array<any>} products
   */
  #storeUpsells(productId, products) {
    const visibleProducts = products.filter((product) => this.#isVisibleUpsellProduct(product)).slice(0, 4);

    if (!visibleProducts.length) {
      this.#clearStoredUpsells();
      return;
    }

    try {
      window.localStorage.setItem(
        CART_DRAWER_UPSELLS_STORAGE_KEY,
        JSON.stringify({ productId: productId.toString() })
      );
    } catch (_error) {
      // Ignore storage failures and keep the in-session drawer update.
    }
  }

  #clearStoredUpsells() {
    try {
      window.localStorage.removeItem(CART_DRAWER_UPSELLS_STORAGE_KEY);
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  /**
   * @param {Array<any>} products
   * @param {{ animate?: boolean, sourceProductId?: string | number }} [options]
   */
  #setUpsellProducts(products, options = {}) {
    const visibleProducts = products.filter((product) => this.#isVisibleUpsellProduct(product)).slice(0, 4);
    const cards = visibleProducts.map((product) => this.#renderUpsellCard(product)).filter((card) => card !== '');
    const markup = cards.join('');

    if (cards.length) {
      this.#currentUpsellProducts = visibleProducts;
      this.#currentUpsellProductId = options.sourceProductId?.toString() || this.#currentUpsellProductId;
    } else {
      this.#currentUpsellProducts = [];
      this.#currentUpsellProductId = null;
    }

    this.refs.dialog?.querySelectorAll('.cart-drawer__upsells-list').forEach((list) => {
      list.innerHTML = markup;
    });

    this.refs.dialog?.toggleAttribute('data-upsells-static', options.animate === false);
    this.#renderUpsellDots(cards.length);
    this.#setUpsellsVisible(cards.length > 0);
  }

  /** @param {boolean} isVisible */
  #setUpsellsVisible(isVisible, options = {}) {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    const alreadyVisible = this.#hasVisibleUpsells();

    if (this.#upsellsHideTimeout) {
      window.clearTimeout(this.#upsellsHideTimeout);
      this.#upsellsHideTimeout = undefined;
    }

    dialog.removeAttribute('data-upsells-exiting');

    if (isVisible) {
      if (!alreadyVisible || options.animate) {
        dialog.toggleAttribute('data-upsells-static', !options.animate);
      }
      dialog.querySelectorAll('.cart-drawer__upsells-desktop, .cart-drawer__upsells-mobile').forEach((element) => {
        element.removeAttribute('hidden');
      });
      return;
    }

    if (alreadyVisible && !isMobileBreakpoint()) {
      dialog.removeAttribute('data-upsells-static');
      dialog.setAttribute('data-upsells-exiting', 'true');
      this.#upsellsHideTimeout = window.setTimeout(() => {
        dialog.querySelectorAll('.cart-drawer__upsells-desktop, .cart-drawer__upsells-mobile').forEach((element) => {
          element.setAttribute('hidden', '');
        });
        dialog.removeAttribute('data-upsells-exiting');
        dialog.setAttribute('data-upsells-static', '');
      }, 320);
      return;
    }

    dialog.setAttribute('data-upsells-static', '');
    dialog.querySelectorAll('.cart-drawer__upsells-desktop, .cart-drawer__upsells-mobile').forEach((element) => {
      element.setAttribute('hidden', '');
    });
  }

  #hasRenderedUpsells() {
    return this.refs.dialog?.querySelector('.cart-drawer__upsell-card') !== null;
  }

  #hasVisibleUpsells() {
    return this.refs.dialog?.querySelector('.cart-drawer__upsells-desktop:not([hidden]), .cart-drawer__upsells-mobile:not([hidden])') !== null;
  }

  /**
   * @param {string | number} productId
   * @param {AbortSignal} signal
   * @returns {Promise<Array<any>>}
   */
  async #fetchRecommendedProducts(productId, signal) {
    return (await this.#fetchRecommendations(productId, 'complementary', signal))
      .filter((product) => this.#isVisibleUpsellProduct(product));
  }

  /**
   * @param {string | number} productId
   * @param {'complementary' | 'related'} intent
   * @param {AbortSignal} signal
   * @returns {Promise<Array<any>>}
   */
  async #fetchRecommendations(productId, intent, signal) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const url = new URL(`${rootUrl}recommendations/products.json`, window.location.origin);
    url.searchParams.set('product_id', productId.toString());
    url.searchParams.set('intent', intent);
    url.searchParams.set('limit', '4');

    const response = await fetch(url, { signal });
    if (!response.ok) return [];

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  /** @param {any} product */
  #isPartsProduct(product) {
    return (product?.type || product?.product_type || '').toString().trim().toLowerCase() === 'parts';
  }

  /** @param {any} product */
  #isHiddenProduct(product) {
    const tags = Array.isArray(product?.tags) ? product.tags : (product?.tags || '').toString().split(',');
    return tags.some((tag) => ['not-new-site', 'hidden-from-search'].includes(tag.toString().trim()));
  }

  /** @param {any} product */
  #isVisibleUpsellProduct(product) {
    return !this.#isPartsProduct(product) && !this.#isHiddenProduct(product);
  }

  /** @param {number} count */
  #renderUpsellDots(count) {
    this.refs.dialog?.querySelectorAll('.cart-drawer__upsells-dots').forEach((dots) => {
      if (count <= 1) {
        dots.innerHTML = '';
        return;
      }

      dots.innerHTML = Array.from({ length: count }, (_, index) => (
        `<button type="button" class="cart-drawer__upsells-dot${index === 0 ? ' cart-drawer__upsells-dot--active' : ''}" data-upsell-dot="${index}" aria-label="Show upsell ${index + 1}"></button>`
      )).join('');
    });
  }

  /** @param {any} product */
  #renderUpsellCard(product) {
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    if (!variant?.id) return '';

    const title = this.#escapeHtml(product.title || '');
    const url = product.url || `/products/${product.handle || ''}`;
    const featuredImage = product.featured_image;
    const image = typeof featuredImage === 'string' ? featuredImage : featuredImage?.src || product.images?.[0] || '';
    const price = this.#formatPrice(variant.price ?? product.price);
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price || 0);
    const showCompareAt = compareAtPrice > Number(variant.price || product.price || 0);
    const bazaarvoiceProductId = product.id?.toString() || '';
    const rating = this.#getProductRating(product);
    const ratingMarkup = rating ? `<div class="cart-drawer__upsell-reviews-fallback" style="--rating: ${this.#escapeAttribute(rating.percentage)}%;"><span class="cart-drawer__upsell-stars" aria-hidden="true"></span>${rating.count ? `<span class="cart-drawer__upsell-review-count">(${this.#escapeHtml(rating.count)})</span>` : ''}</div>` : '';

    return `
      <article class="cart-drawer__upsell-card">
        <div class="cart-drawer__upsell-main">
          <a class="cart-drawer__upsell-media" href="${this.#escapeAttribute(url)}" tabindex="-1">
            ${image ? `<img loading="lazy" src="${this.#escapeAttribute(this.#imageUrl(image, 160))}" alt="${title}">` : ''}
          </a>
          <div class="cart-drawer__upsell-copy">
            <a class="cart-drawer__upsell-title" href="${this.#escapeAttribute(url)}">${title}</a>
            <div class="cart-drawer__upsell-reviews product-rating" data-bv-show="inline_rating" data-bv-product-id="${this.#escapeAttribute(bazaarvoiceProductId)}" data-bv-redirect-url="${this.#escapeAttribute(url)}"></div>
            ${ratingMarkup}
          </div>
        </div>
        <div class="cart-drawer__upsell-bottom">
          <span class="cart-drawer__upsell-prices">
            <span class="cart-drawer__upsell-price">${price}</span>
            ${showCompareAt ? `<s>${this.#formatPrice(compareAtPrice)}</s>` : ''}
          </span>
          <form method="post" action="${Theme.routes.cart_add_url}" class="cart-drawer__upsell-form">
            <input type="hidden" name="id" value="${this.#escapeAttribute(variant.id.toString())}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="cart-drawer__upsell-add">
              <span aria-hidden="true">+</span>
              <span class="cart-drawer__upsell-add-label">Add</span>
              <span class="cart-drawer__upsell-spinner" aria-hidden="true"></span>
            </button>
          </form>
        </div>
      </article>
    `;
  }

  /** @param {any} product */
  #getProductRating(product) {
    const rating = Number(product.rating || product.reviews_rating || product.metafields?.reviews?.rating?.value?.rating || 0);
    if (!rating) return null;

    const count = product.rating_count || product.reviews_rating_count || product.metafields?.reviews?.rating_count || '';
    return {
      percentage: Math.min(Math.max(rating * 20, 0), 100).toString(),
      count: count?.toString?.() || '',
    };
  }

  #snapshotUpsellRatings() {
    return Array.from(this.querySelectorAll('.cart-drawer__upsell-reviews[data-bv-product-id]')).reduce((ratings, element) => {
      const productId = element.getAttribute('data-bv-product-id');
      if (productId && element.innerHTML.trim()) ratings.set(productId, element.innerHTML);
      return ratings;
    }, new Map());
  }

  /** @param {Map<string, string>} ratings */
  #restoreUpsellRatings(ratings) {
    if (!ratings.size) return;

    this.querySelectorAll('.cart-drawer__upsell-reviews[data-bv-product-id]').forEach((element) => {
      const productId = element.getAttribute('data-bv-product-id');
      const html = productId ? ratings.get(productId) : undefined;
      if (html && !element.innerHTML.trim()) element.innerHTML = html;
    });
  }

  /** @param {Map<string, string>} ratings */
  #scheduleUpsellRatingsRestore(ratings) {
    if (!ratings.size) return;

    window.requestAnimationFrame(() => this.#restoreUpsellRatings(ratings));
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.#restoreUpsellRatings(ratings)));
    window.setTimeout(() => this.#restoreUpsellRatings(ratings), 300);
  }

  /** @param {number | string} price */
  #formatPrice(price) {
    const value = Number(price) || 0;
    return formatMoney(value, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD').replace(/([.,]00)(?!\d)/, '');
  }

  /** @param {string} url */
  #imageUrl(url, width) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  /** @param {string} value */
  #escapeHtml(value) {
    return value.replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    }[character] || character));
  }

  /** @param {string} value */
  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : undefined)
      .filter((sectionId) => Boolean(sectionId));
  }

  /**
   * @param {Record<string, string> | undefined} sections
   * @param {string[]} sectionIds
   */
  #getCartItemCountFromSections(sections, sectionIds) {
    if (!sections) return null;

    for (const sectionId of sectionIds) {
      const sectionHtml = sections[sectionId];
      if (!sectionHtml) continue;

      const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const count = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent;
      if (count) return Number(count);
    }

    return null;
  }

  /**
   * @param {Element | null} button
   * @param {'loading' | 'added' | 'error'} state
   */
  #setUpsellButtonState(button, state) {
    if (!(button instanceof HTMLButtonElement)) return;

    const label = button.querySelector('.cart-drawer__upsell-add-label');

    if (state === 'loading') {
      button.style.width = `${button.offsetWidth}px`;
    } else {
      button.style.width = '';
    }

    button.dataset.state = state;
    button.disabled = state !== 'error';

    if (state === 'added') {
      if (label) label.textContent = 'Added';
      window.setTimeout(() => {
        if (!button.isConnected || button.dataset.state !== 'added') return;

        button.dataset.state = 'idle';
        button.disabled = false;
        button.style.width = '';
        if (label) label.textContent = 'Add';
      }, 1800);
    }
    if (state === 'error') {
      if (label) label.textContent = 'Error';
      window.setTimeout(() => {
        button.dataset.state = 'idle';
        button.disabled = false;
        button.style.width = '';
        if (label) label.textContent = 'Add';
      }, 1500);
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    document.querySelector('.cart-drawer__dialog')?.removeAttribute('data-added-state');
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    document.querySelector('.cart-drawer__dialog')?.removeAttribute('data-added-state');
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
